import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { getBalance } from '../services/BallsService';
import './ScrubMarketPage.css';

/* ===== TYPES ===== */

interface ScrubPlayer {
  id: string;
  name: string;
  overall: number;
  position: string;
  team_id: string;
  teamName: string;
  price: number;
}

interface TeamFromDB {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
  tier: number;
  league_id: string;
  owner_id?: string;
}

type SortKey = 'overall' | 'price' | 'name' | 'position';
type SortDir = 'asc' | 'desc';

const FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_URL
  ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`
  : '';

const MAX_PURCHASES_PER_SEASON = 3;

const POSITIONS_ALL = ['All', 'P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF'];

/* ===== HELPERS ===== */

function getOvrColor(ovr: number): string {
  if (ovr >= 80) return '#f59e0b';
  if (ovr >= 70) return '#22c55e';
  if (ovr >= 60) return '#3b82f6';
  if (ovr >= 50) return '#9ca3af';
  return '#6b7280';
}

function getOvrLabel(ovr: number): string {
  if (ovr >= 80) return 'Elite';
  if (ovr >= 70) return 'Great';
  if (ovr >= 60) return 'Good';
  if (ovr >= 50) return 'Average';
  return 'Raw';
}

/* ===== COMPONENT ===== */

interface ScrubMarketPageProps {
  onNavigate?: (page: string) => void;
  userData?: any;
}

const ScrubMarketPage: React.FC<ScrubMarketPageProps> = ({ onNavigate, userData }) => {
  // Auth & team
  const [ownedTeams, setOwnedTeams] = useState<TeamFromDB[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<TeamFromDB | null>(null);
  const [ballsBalance, setBallsBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  // Scrub list
  const [scrubs, setScrubs] = useState<ScrubPlayer[]>([]);
  const [scrubsLoading, setScrubsLoading] = useState(false);

  // Filters & sort
  const [posFilter, setPosFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('overall');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Purchase state
  const [remainingPurchases, setRemainingPurchases] = useState<number>(MAX_PURCHASES_PER_SEASON);
  const [confirmPlayer, setConfirmPlayer] = useState<ScrubPlayer | null>(null);
  const [buying, setBuying] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  /* ===== LOAD TEAMS & BALANCE ===== */

  useEffect(() => {
    loadTeams();
  }, [userData?.id]);

  async function loadTeams() {
    setLoading(true);
    if (!isSupabaseConfigured() || !supabase) { setLoading(false); return; }

    const userId = userData?.id;
    if (userId) {
      const balance = await getBalance(userId);
      setBallsBalance(balance);

      const { data: owned } = await supabase
        .from('teams')
        .select('*')
        .eq('owner_id', userId);
      setOwnedTeams(owned || []);

      if (owned && owned.length > 0 && !selectedTeam) {
        setSelectedTeam(owned[0]);
      }
    }
    setLoading(false);
  }

  /* ===== LOAD SCRUBS ===== */

  useEffect(() => {
    if (selectedTeam) {
      loadScrubs();
      loadRemainingPurchases();
    }
  }, [selectedTeam]);

  async function loadScrubs() {
    if (!selectedTeam || !FUNCTIONS_URL) return;
    setScrubsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${FUNCTIONS_URL}/buy-scrub`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(session ? { 'Authorization': `Bearer ${session.access_token}` } : {}) },
        body: JSON.stringify({ action: 'list-scrubs', leagueId: selectedTeam.league_id }),
      });
      const data = await res.json();
      if (data.scrubs) {
        setScrubs(data.scrubs);
      }
    } catch (err) {
      console.error('Error loading scrubs:', err);
      showToast('error', 'Failed to load scrub market');
    }
    setScrubsLoading(false);
  }

  async function loadRemainingPurchases() {
    if (!selectedTeam || !isSupabaseConfigured() || !supabase) return;

    try {
      // Count scrub purchases this season for the user's team
      // We use the balls_transactions table to count scrub_purchase transactions
      const { data: txns } = await supabase
        .from('balls_transactions')
        .select('id')
        .eq('user_id', userData?.id)
        .eq('reference_type', 'scrub_purchase');

      const used = txns?.length || 0;
      setRemainingPurchases(Math.max(0, MAX_PURCHASES_PER_SEASON - used));
    } catch {
      // If we can't load, just show max
      setRemainingPurchases(MAX_PURCHASES_PER_SEASON);
    }
  }

  /* ===== PRICE CHECK ===== */

  async function handlePriceCheck(player: ScrubPlayer) {
    if (!FUNCTIONS_URL) return;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${FUNCTIONS_URL}/buy-scrub`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(session ? { 'Authorization': `Bearer ${session.access_token}` } : {}) },
        body: JSON.stringify({ action: 'price-check', playerId: player.id }),
      });
      const data = await res.json();
      if (data.price !== undefined) {
        // Update the player's price from the server
        setConfirmPlayer({ ...player, price: data.price });
      } else {
        setConfirmPlayer(player);
      }
    } catch {
      setConfirmPlayer(player);
    }
  }

  /* ===== BUY SCRUB ===== */

  async function handleBuy() {
    if (!confirmPlayer || !selectedTeam || !isSupabaseConfigured()) return;
    setBuying(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        showToast('error', 'Not authenticated. Please sign in.');
        setBuying(false);
        return;
      }

      const res = await fetch(`${FUNCTIONS_URL}/buy-scrub`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'buy',
          playerId: confirmPlayer.id,
          teamId: selectedTeam.id,
        }),
      });

      const result = await res.json();

      if (result.error) {
        showToast('error', result.error);
      } else {
        const newRemaining = result.remainingPurchases ?? Math.max(0, remainingPurchases - 1);
        setRemainingPurchases(newRemaining);
        showToast('success', `Acquired ${result.player?.name || confirmPlayer.name} for ${result.price || confirmPlayer.price} $BALLS!`);
        setConfirmPlayer(null);
        // Refresh balance
        if (userData?.id) {
          const balance = await getBalance(userData.id);
          setBallsBalance(balance);
        }
        // Refresh scrub list (player no longer available)
        loadScrubs();
      }
    } catch (err: any) {
      showToast('error', err.message || 'Purchase failed. Try again.');
    }
    setBuying(false);
  }

  /* ===== TOAST ===== */

  function showToast(type: 'success' | 'error', text: string) {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  }

  /* ===== FILTER & SORT ===== */

  const filteredScrubs = React.useMemo(() => {
    let list = [...scrubs];

    // Position filter
    if (posFilter !== 'All') {
      list = list.filter(s => s.position === posFilter);
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.teamName.toLowerCase().includes(q) ||
        s.position.toLowerCase().includes(q)
      );
    }

    // Sort
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === 'overall') return (a.overall - b.overall) * dir;
      if (sortKey === 'price') return (a.price - b.price) * dir;
      if (sortKey === 'name') return a.name.localeCompare(b.name) * dir;
      if (sortKey === 'position') return a.position.localeCompare(b.position) * dir;
      return 0;
    });

    return list;
  }, [scrubs, posFilter, searchQuery, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const sortIcon = (key: SortKey) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const canBuy = remainingPurchases > 0 && ballsBalance > 0;

  /* ===== RENDER ===== */

  if (loading) {
    return (
      <div className="scrub-market-page">
        <div className="scrub-market-loading">Loading Scrub Market...</div>
      </div>
    );
  }

  if (!selectedTeam) {
    return (
      <div className="scrub-market-page">
        <div className="scrub-market-empty">
          <div className="scrub-market-empty-icon">🏟️</div>
          <h2>No Team Found</h2>
          <p>You need a team in a league to buy scrubs.</p>
          <button className="scrub-market-gold-btn" onClick={() => onNavigate?.('/my-teams')}>
            Go to Owner's Office
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="scrub-market-page">
      {/* Header */}
      <div className="scrub-market-header">
        <div>
          <h1>🧹 Scrub Market</h1>
          <p className="scrub-market-subtitle">
            Buy low-overall players from AI teams to fill your roster
          </p>
        </div>
        <div className="scrub-market-header-right">
          <div className="scrub-market-balance">
            <span className="scrub-market-balance-label">YOUR BALANCE</span>
            <span className="scrub-market-balance-amount">{ballsBalance.toLocaleString()} $BALLS</span>
          </div>
          <div className="scrub-market-purchases">
            <span className="scrub-market-purchases-label">PURCHASES LEFT</span>
            <span className={`scrub-market-purchases-amount ${remainingPurchases === 0 ? 'exhausted' : ''}`}>
              {remainingPurchases}/{MAX_PURCHASES_PER_SEASON}
            </span>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`scrub-market-toast scrub-market-toast-${toast.type}`}>
          {toast.type === 'success' ? '✅' : '❌'} {toast.text}
        </div>
      )}

      {/* Filters & Search */}
      <div className="scrub-market-controls">
        <div className="scrub-market-search-wrap">
          <input
            type="text"
            className="scrub-market-search"
            placeholder="Search players or teams..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="scrub-market-pos-tabs">
          {POSITIONS_ALL.map(pos => (
            <button
              key={pos}
              className={`scrub-market-pos-tab ${posFilter === pos ? 'active' : ''}`}
              onClick={() => setPosFilter(pos)}
            >
              {pos}
            </button>
          ))}
        </div>
      </div>

      {/* Scrub list */}
      {scrubsLoading ? (
        <div className="scrub-market-loading">Loading scrubs...</div>
      ) : filteredScrubs.length === 0 ? (
        <div className="scrub-market-empty-list">
          <div className="scrub-market-empty-icon">🔍</div>
          <p>{scrubs.length === 0 ? 'No scrubs available in your league' : 'No scrubs match your filters'}</p>
        </div>
      ) : (
        <>
          {/* Sort header */}
          <div className="scrub-market-sort-bar">
            <button className="scrub-market-sort-btn" onClick={() => toggleSort('name')}>
              Name{sortIcon('name')}
            </button>
            <button className="scrub-market-sort-btn" onClick={() => toggleSort('position')}>
              POS{sortIcon('position')}
            </button>
            <button className="scrub-market-sort-btn" onClick={() => toggleSort('overall')}>
              OVR{sortIcon('overall')}
            </button>
            <button className="scrub-market-sort-btn" onClick={() => toggleSort('price')}>
              Price{sortIcon('price')}
            </button>
            <span className="scrub-market-sort-col">Team</span>
            <span className="scrub-market-sort-col-action">Action</span>
          </div>

          <div className="scrub-market-list">
            {filteredScrubs.map(scrub => {
              const canAfford = ballsBalance >= scrub.price;
              const disabled = !canBuy || !canAfford;

              return (
                <div key={scrub.id} className="scrub-market-card">
                  <div className="scrub-market-card-name">{scrub.name}</div>
                  <div className="scrub-market-card-pos">
                    <span className="scrub-market-pos-badge">{scrub.position}</span>
                  </div>
                  <div className="scrub-market-card-ovr">
                    <span className="scrub-market-ovr-value" style={{ color: getOvrColor(scrub.overall) }}>
                      {scrub.overall}
                    </span>
                    <span className="scrub-market-ovr-label">{getOvrLabel(scrub.overall)}</span>
                  </div>
                  <div className="scrub-market-card-price">
                    <span className="scrub-market-price-value">{scrub.price.toLocaleString()}</span>
                    <span className="scrub-market-price-unit">$BALLS</span>
                  </div>
                  <div className="scrub-market-card-team">{scrub.teamName}</div>
                  <div className="scrub-market-card-action">
                    <button
                      className={`scrub-market-buy-btn ${disabled ? 'disabled' : ''}`}
                      disabled={disabled}
                      onClick={() => handlePriceCheck(scrub)}
                    >
                      {remainingPurchases === 0
                        ? 'Maxed Out'
                        : !canAfford
                          ? 'Can\'t Afford'
                          : 'Buy'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Info section */}
      <div className="scrub-market-info">
        <h3>💡 How Scrub Market Works</h3>
        <div className="scrub-market-info-grid">
          <div className="scrub-market-info-step">
            <span className="scrub-market-info-num">1</span>
            <span>Browse scrub players (OVR ≤ 45) from AI teams in your league</span>
          </div>
          <div className="scrub-market-info-step">
            <span className="scrub-market-info-num">2</span>
            <span>Price is based on Overall rating (OVR × 50 $BALLS)</span>
          </div>
          <div className="scrub-market-info-step">
            <span className="scrub-market-info-num">3</span>
            <span>You can buy up to {MAX_PURCHASES_PER_SEASON} scrubs per season</span>
          </div>
          <div className="scrub-market-info-step">
            <span className="scrub-market-info-num">4</span>
            <span>Payment is split: 80% to the AI team, 20% to the treasury</span>
          </div>
        </div>
      </div>

      {/* Confirm Purchase Modal */}
      {confirmPlayer && (
        <div className="scrub-market-modal-overlay" onClick={() => !buying && setConfirmPlayer(null)}>
          <div className="scrub-market-modal" onClick={e => e.stopPropagation()}>
            <div className="scrub-market-modal-header">
              <h3>Confirm Purchase</h3>
              <button
                className="scrub-market-modal-close"
                onClick={() => !buying && setConfirmPlayer(null)}
                disabled={buying}
              >
                ×
              </button>
            </div>
            <div className="scrub-market-modal-body">
              <div className="scrub-market-modal-player">
                <span className="scrub-market-modal-pos-badge">{confirmPlayer.position}</span>
                <div className="scrub-market-modal-player-info">
                  <span className="scrub-market-modal-player-name">{confirmPlayer.name}</span>
                  <span className="scrub-market-modal-player-detail">
                    OVR <span style={{ color: getOvrColor(confirmPlayer.overall) }}>{confirmPlayer.overall}</span>
                    {' · '}
                    from {confirmPlayer.teamName}
                  </span>
                </div>
              </div>
              <div className="scrub-market-modal-price-row">
                <span>Price:</span>
                <span className="scrub-market-modal-price">{confirmPlayer.price.toLocaleString()} $BALLS</span>
              </div>
              <div className="scrub-market-modal-balance-row">
                <span>Your balance after:</span>
                <span className={ballsBalance - confirmPlayer.price < 0 ? 'scrub-market-modal-negative' : ''}>
                  {(ballsBalance - confirmPlayer.price).toLocaleString()} $BALLS
                </span>
              </div>
              <div className="scrub-market-modal-remaining-row">
                <span>Remaining purchases after:</span>
                <span>{Math.max(0, remainingPurchases - 1)}/{MAX_PURCHASES_PER_SEASON}</span>
              </div>
            </div>
            <div className="scrub-market-modal-footer">
              <button
                className="scrub-market-cancel-btn"
                onClick={() => setConfirmPlayer(null)}
                disabled={buying}
              >
                Cancel
              </button>
              <button
                className="scrub-market-confirm-btn"
                onClick={handleBuy}
                disabled={buying}
              >
                {buying ? 'Processing...' : `Buy for ${confirmPlayer.price.toLocaleString()} $BALLS`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrubMarketPage;
